import { fabric } from "fabric";
import { v4 } from "uuid";
import { CanvasHandler } from "../../handlers";
import { FabricEvent, FabricObject } from "../../types/FabricCustomType";
import { IObjectDesingner } from "../../types/HandlerObjectType";
import { ShapeProperties } from "./properties";
import {iconStage} from "../../constants/Icons"

class Polygon extends ShapeProperties implements IObjectDesingner {
  constructor(canvasHandler: CanvasHandler) {
    super(canvasHandler);
  }
  onRotated(event: FabricEvent) {
    throw new Error("Method not implemented.");
  }

  public onInit() {
    this.canvasHandler.interactionHandler.drawing("polygon");
    this.canvasHandler.pointArray = [];
    this.canvasHandler.lineArray = [];
    this.canvasHandler.activeLine = null;
    this.canvasHandler.activeShapePolygon = null;
  }

  onMouseDblClick(event: FabricEvent<Event>) {
    return;
  }

  public onMousedown(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    const { target } = event;
    if (
      target &&
      this.canvasHandler.pointArray.length &&
      target.id === this.canvasHandler.pointArray[0].id
    ) {
      this.generate(this.canvasHandler.pointArray);
    } else {
      this.addPoint(x, y);
    }
  }

  public onMousemove(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    if (this.canvasHandler.activeLine && this.canvasHandler.activeLine.class === "line") {
      this.canvasHandler.activeLine.set({ x2: x, y2: y });
      const points = this.canvasHandler.activeShapePolygon.get("points");
      points[this.canvasHandler.pointArray.length] = { x, y };
      this.canvasHandler.activeShapePolygon.set({ points });
      this.canvasHandler.canvas.requestRenderAll();
    }
  }

  public onMouseup(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    return;
  }

  private addPoint(x: number, y: number) {
    const circle = new fabric.Circle({
      radius: 3,
      fill: "#cccccc",
      stroke: "#727272",
      strokeWidth: 0.5,
      left: x,
      top: y,
      selectable: false,
      hasBorders: false,
      hasControls: false,
      originX: "center",
      originY: "center",
      hoverCursor: "pointer",
    }) as FabricObject<fabric.Circle>;
    circle.set({ id: v4() });
    if (!this.canvasHandler.pointArray.length) circle.set({ fill: "red" });
    const points = [x, y, x, y];
    const line = new fabric.Line(points, {
      strokeWidth: 4,
      fill: "#cccccc",
      stroke: "#727272",
      originX: "center",
      originY: "center",
      selectable: false,
      hasBorders: false,
      hasControls: false,
      evented: false,
    }) as FabricObject<fabric.Line>;
    line.set({ class: "line" });
    if (this.canvasHandler.activeShapePolygon) {
      const activeShapePoints = this.canvasHandler.activeShapePolygon.get("points");
      activeShapePoints.push({ x, y });
      const polygon = new fabric.Polygon(activeShapePoints, {
        stroke: "#727272",
        strokeWidth: 4,
        fill: "#cccccc",
        opacity: 0.1,
        selectable: false,
        hasBorders: false,
        hasControls: false,
        evented: false,
      });
      this.canvasHandler.canvas.remove(this.canvasHandler.activeShapePolygon);
      this.canvasHandler.canvas.add(polygon);
      this.canvasHandler.activeShapePolygon = polygon;
      this.canvasHandler.canvas.renderAll();
    } else {
      const polyPoint = [{ x, y }];
      const polygon = new fabric.Polygon(polyPoint, {
        stroke: "#727272",
        strokeWidth: 1,
        fill: "#cccccc",
        opacity: 0.1,
        selectable: false,
        hasBorders: false,
        hasControls: false,
        evented: false,
      });
      this.canvasHandler.activeShapePolygon = polygon;
      this.canvasHandler.canvas.add(polygon);
    }
    this.canvasHandler.activeLine = line;
    this.canvasHandler.pointArray.push(circle);
    this.canvasHandler.lineArray.push(line);
    this.canvasHandler.canvas.add(line);
    this.canvasHandler.canvas.add(circle);
  }

  private generate(pointArray: FabricObject<fabric.Circle>[]) {
    const points = [] as any[];
    const id = v4();
    const { editable } = this.canvasHandler;

    pointArray.forEach((point) => {
      points.push({ x: point.left, y: point.top });
      this.canvasHandler.canvas.remove(point);
    });
    this.canvasHandler.lineArray.forEach((line) => {
      this.canvasHandler.canvas.remove(line);
    });

    this.canvasHandler.canvas
      .remove(this.canvasHandler.activeShapePolygon)
      .remove(this.canvasHandler.activeLine);
    this.canvasHandler.activeShapePolygon = new fabric.Polygon(points, {
      type: "polygon",
      stroke: "#727272",
      strokeWidth: 3,
      fill: "#cccccc",
      opacity: 1,
      objectCaching: !this.canvasHandler.editable,
      name: "New polygon",
      hasControls: editable,
      hasBorders: editable,
      selectable: editable,
      lockMovementX: !editable,
      lockMovementY: !editable,
      hoverCursor: !editable ? "pointer" : "move",
      perPixelTargetFind: true,
    }) as FabricObject<fabric.Polygon>;

    this.canvasHandler.activeShapePolygon.set({
      id,
      superType: "drawing",
    });

    this.canvasHandler.objText = new fabric.Text("Label", {
      fontSize: 16,
      left: this.canvasHandler.originPointer.x,
      top: this.canvasHandler.originPointer.y,
      angle: 0,
      fill: "#727272",
      stroke: "#727272",
      strokeWidth: 0,
      fontFamily: "Roboto",
      opacity: 1,
      type: "textbox",
      hasControls: editable,
      hasBorders: editable,
      selectable: editable,
      lockMovementX: !editable,
      lockMovementY: !editable,
      hoverCursor: !editable ? "pointer" : "move",
      text: "?",
      visible: false,
    }) as FabricObject<fabric.Text>;

    this.canvasHandler.objText.set({
      id: id,
      superType: "textbox",
      container: this.canvasHandler.container.id,
      editable,
      displayedLabel: false,
    });

    let svgConverted, serializer, svgStr;

    let svgEl = iconStage;

    svgConverted = document.createRange().createContextualFragment(svgEl);
    serializer = new XMLSerializer();
    svgStr = serializer.serializeToString(svgConverted);

    let containerId = this.canvasHandler.container.id;
    let loadedObject = this.canvasHandler.objIcon;
    let x = this.canvasHandler.originPointer.x;
    let y = this.canvasHandler.originPointer.y;

    fabric.loadSVGFromString(svgStr, function(objects, options) {
      loadedObject = fabric.util.groupSVGElements(objects, options);

      loadedObject.set({
        id: id,
        hasControls: editable,
        hasBorders: editable,
        selectable: editable,
        lockMovementX: !editable,
        lockMovementY: !editable,
        hoverCursor: !editable ? "pointer" : "move",
        container: containerId,
        editable: editable,
        left: x,
        top: y,
        originX: "left",
        originY: "top",
        opacity: 1,
        objectCaching: !editable,
        superType: "svg",
        type: "svg",
        name: "New icon SVG",
        svg: svgEl,
        loadType: "svg",
        visible: false,
        scaled: 1,
        originHeight: loadedObject.height,
        originWidth: loadedObject.width,
        rotated: 0
      });
    });

    this.canvasHandler.objIcon = loadedObject;

    let group = new fabric.Group(
      [this.canvasHandler.activeShapePolygon, this.canvasHandler.objText, this.canvasHandler.objIcon],
      {
        left: this.canvasHandler.activeShapePolygon.left,
        top: this.canvasHandler.activeShapePolygon.top,
        hasControls: editable,
        hasBorders: editable,
        type: "group",
        subTargetCheck: true,
      }
    ) as FabricObject<fabric.Group>;

    group.set({
      id: id,
      superType: "polygon",
      text: "Label",
      displayedLabel: false,
      typeIcon: "",
      visibleIcon: false,
      visibleText: false,
      hoverCursor: "crosshair",
      selectable: false
    });

    group.setControlsVisibility({
      tl: false, //top-left
      mt: false, // middle-top
      tr: false, //top-right
      ml: false, //middle-left
      mr: false, //middle-right
      bl: false, // bottom-left
      mb: false, //middle-bottom
      br: false, //bottom-right
      mtr: true,
    });

    this.canvasHandler.canvas.remove(this.canvasHandler.activeShapePolygon);
    this.canvasHandler.objIcon = null;
    this.canvasHandler.canvas.add(group);

    group.item(2).left = 0;
    group.item(2).top = 0;

    const widthIcon = group.item(2).width ?? 0;
    const heightIcon = group.item(2).height ?? 0;
    group.item(2).set({
      left: group.item(2).left - (widthIcon / 2),
      top: group.item(2).top - (heightIcon / 2),
      fill: "#727272",
    });

    group.item(1).left = 1;
    group.item(1).top = 1;

    const widthText = group.item(1).width ?? 0;
    const heightText = group.item(1).height ?? 0;

    group.item(1).set({
      left: group.item(1).left - (widthText / 2),
      top: group.item(1).top - (heightText / 2),
    });

    this.canvasHandler.pointArray = [];
    this.canvasHandler.activeLine = null;
    this.canvasHandler.activeShapePolygon = null;
    this.canvasHandler.canvas.discardActiveObject();
  }
}
export default Polygon;
